import { useEffect, useMemo, useReducer, useState } from 'react';
import Dropzone, { useDropzone } from 'react-dropzone';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, Button, FormControlLabel, Grid, Switch } from '@mui/material';
import { useSelector } from 'react-redux';

import PdfCard from '../common/PdfCard';
import PurchaserForm from '../common/forms/PurchaserForm';
import LoadingLogo from '../common/LoadingLogo';
import { FlexEnd } from '../../commonStyles';
import { useAppDispatch } from '../../app/hooks';
import { selectProject } from '../../features/project/projectSlice';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '../../utils/Constants';
import { purchasersReducer } from '../unitMain/assignment/reducers';
import { purchaserToForm, purchaserUpdateRecord, usePurchaserFormHandlers, GETPURCHASER, UPDATEPURCHASER, UPLOADID, DELETEID } from './purchaserShared';

// Edits one of a worksheet's purchasers, saving straight to the purchaser's record
const EditWorksheetPurchaser = (props: ChildProps) => {
  const { purchaserId, onSaved, onCancel } = props;
  const project = useSelector(selectProject);
  const storeDispatch = useAppDispatch();
  const [purchasersState, purchasersDispatch] = useReducer(purchasersReducer, []);
  const [errors, setErrors] = useState<any>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const handlers = usePurchaserFormHandlers(purchasersState, purchasersDispatch, setErrors);

  const [getPurchaser] = useLazyQuery(GETPURCHASER, { fetchPolicy: 'network-only' });
  const [updatePurchaser] = useMutation(UPDATEPURCHASER);
  const [uploadID] = useMutation(UPLOADID);
  const [deleteID] = useMutation(DELETEID);

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/jpeg, image/png, application/pdf',
  });
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const loadPurchaser = async () => {
    const { data } = await getPurchaser({ variables: { _id: purchaserId } });
    if (!data?.purchaserById) {
      storeDispatch(showErrorSnackbar('Unable to load this purchaser'));
      return null;
    }
    purchasersDispatch({ type: 'SWAP', payload: [purchaserToForm(data.purchaserById, project._id)] });
    return data.purchaserById;
  };

  useEffect(() => {
    loadPurchaser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaserId]);

  const pInfo: any = purchasersState[0];

  const handleDrop = (acceptedFiles: any) => {
    if (acceptedFiles.length === 0) {
      storeDispatch(showErrorSnackbar('This file type is not allowed'));
      return;
    }
    const file = acceptedFiles[0];
    const fileReader: any = new FileReader();
    fileReader.onloadend = () => {
      purchasersDispatch({
        type: 'UPDATE',
        payload: {
          index: 0,
          field: 'identifications',
          value: [...pInfo.identifications, { name: file.name, file, result: fileReader.result }],
        },
      });
    };
    fileReader.readAsDataURL(file);
  };

  // Saved IDs are deleted from the purchaser's record right away (PdfCard already asked to confirm);
  // IDs added here but not saved yet are just removed
  const deleteImage = async (id: any, title: string, idIndex: number) => {
    const identification = pInfo.identifications[idIndex];
    if (identification?._id) {
      try {
        await deleteID({ variables: { _id: purchaserId, documentId: identification._id, deleteFile: true, type: 'identification' } });
      } catch (error: any) {
        storeDispatch(showErrorSnackbar(error.message));
        return;
      }
    }
    purchasersDispatch({
      type: 'UPDATE',
      payload: { index: 0, field: 'identifications', value: pInfo.identifications.filter((_: any, index: number) => index !== idIndex) },
    });
  };

  const handleSave = async () => {
    if (errors) {
      storeDispatch(showErrorSnackbar(errors));
      return;
    }
    if (!pInfo.email || !pInfo.lastName) {
      storeDispatch(showErrorSnackbar('Purchaser email and last name are required'));
      return;
    }
    setSaving(true);
    try {
      await updatePurchaser({ variables: { _id: purchaserId, record: purchaserUpdateRecord(pInfo, project._id) } });
      for (const identification of pInfo.identifications.filter((id: any) => id.file)) {
        await uploadID({ variables: { id: purchaserId, name: identification.file.name, file: identification.file } });
      }
      const saved = await loadPurchaser();
      storeDispatch(showSuccessSnackbar('Purchaser has been updated!'));
      if (saved) onSaved(saved);
    } catch (error: any) {
      storeDispatch(showErrorSnackbar(error.message));
    } finally {
      setSaving(false);
    }
  };

  if (!pInfo) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <LoadingLogo />
      </Box>
    );
  }

  return (
    <Box>
      <Dropzone onDrop={handleDrop} accept="image/jpg, image/jpeg, image/png, application/pdf">
        {({ getRootProps, getInputProps }) => (
          <section>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p>Drag and Drop or Upload Images</p>
            </div>
          </section>
        )}
      </Dropzone>
      {pInfo.identifications.length ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {pInfo.identifications.map((attachment: any, idIndex: number) => (
            <Grid key={idIndex} item md={4} sm={6} xs={12}>
              <PdfCard
                file={attachment.result || attachment.getUrl}
                title={attachment.name}
                id={idIndex}
                handleDelete={deleteImage}
                download={true}
                index={idIndex}
              />
            </Grid>
          ))}
        </Grid>
      ) : null}
      <Box sx={{ my: 2 }}>
        <FormControlLabel
          control={<Switch checked={!!pInfo.corp} onChange={(e: any) => handlers.handleCorpChange(e, 0)} />}
          label={'Corp'}
        />
      </Box>
      <PurchaserForm
        pInfo={pInfo}
        handleTextInput={handlers.handleTextInput}
        handleDateChange={handlers.handleDateChange}
        handleEndvestorInput={handlers.handleEndvestorInput}
        handleIdTypeInput={handlers.handleIdTypeInput}
        handleIdIssuedByInput={handlers.handleIdIssuedByInput}
        handleStreetAddress={handlers.handleStreetAddress}
        handleDetails={handlers.handleDetails}
        handleDeleteSigningOfficer={handlers.handleDeleteSigningOfficer}
        handleSigningOfficerInput={handlers.handleSigningOfficerInput}
        addSigningOfficer={handlers.addSigningOfficer}
        handleDob={handlers.handleDob}
        type={0}
        controlled={true}
      />
      <FlexEnd sx={{ mt: 2, gap: 1 }}>
        <Button variant="outlined" disabled={saving} onClick={onCancel} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="success" disabled={saving} onClick={handleSave} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving...' : 'Save Purchaser'}
        </Button>
      </FlexEnd>
    </Box>
  );
};

interface ChildProps {
  purchaserId: string;
  onSaved: (purchaser: any) => void;
  onCancel: () => void;
}

export default EditWorksheetPurchaser;
